/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { useNavigate } from '@/hooks/use-navigate';
import { useAnalytics } from '@/hooks/use-analytics';
import { AUTH_CONVERSION_PARAM, parseAuthConversionKind } from '@/analytics/auth-conversion-flag';

/**
 * RGBARRY-CUSTOMIZATION (W-23955510 / GTM remediation): app-shell consumer for the post-auth
 * conversion flag. When a login/registration server action (or an OTP/passkey client completion)
 * lands the shopper on a URL carrying `?authEvent=sign_up` or `?authEvent=login`, fire the matching
 * GA4 conversion event exactly once and strip the param so a refresh cannot re-fire it.
 *
 * This mirrors <WishlistMergeToast>: it reads a one-shot URL flag set by the auth-success redirect
 * target, not auth/session state — which is why it cleanly distinguishes a fresh sign_up/login from
 * an already-authenticated page load (auth state alone cannot). Mounted once in the app shell so it
 * covers every auth terminal (full-page login/signup, login modal — whose fetcher follows the
 * redirect —, and the OTP/passkey navigations). Renders nothing.
 *
 * Known gap: social-IDP logins round-trip through an external provider + callback route and do not
 * carry the flag today; documented in docs/ASSESSMENT-GOOGLE-TAG-MANAGER.md.
 *
 * Trust boundary: `?authEvent=` is a client-readable, shareable/forgeable hint — anyone can craft a
 * URL that fires a sign_up/login conversion, so treat these as *marketing/GA4 signals only*, never as
 * proof of an authenticated action for entitlement/security. It carries no PII (just the literal
 * `sign_up`/`login`). Keep it that way: do not extend this flag to carry email/customerId — and note
 * the separate `verificationSent` redirect that puts `email=` in the URL leaks PII into GA4
 * `page_location`; flagged for the owners / TrueVault consent review (see ASSESSMENT doc).
 */
export function AuthConversionTracker(): null {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { trackSignUp, trackLogin } = useAnalytics();
    const firedFor = useRef<string | null>(null);

    useEffect(() => {
        const kind = parseAuthConversionKind(searchParams.get(AUTH_CONVERSION_PARAM));
        if (!kind) {
            return;
        }
        // Dedupe within a mount: key on path + kind so a second identical flag (e.g. StrictMode
        // double-invoke, or navigating back) doesn't double-count the conversion.
        const key = `${location.pathname}?${kind}`;
        if (firedFor.current === key) {
            return;
        }
        firedFor.current = key;

        if (kind === 'sign_up') {
            void trackSignUp();
        } else {
            void trackLogin();
        }

        // Strip the flag (replace, no history entry) so a refresh/back does not re-fire.
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete(AUTH_CONVERSION_PARAM);
        const search = nextParams.toString();
        void navigate(`${location.pathname}${search ? `?${search}` : ''}`, { replace: true });
    }, [searchParams, location.pathname, navigate, trackSignUp, trackLogin]);

    return null;
}
